import { isAxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SuccessToast } from '../../../adminComponents/Toasts/Toasts';
import { api } from '../../../api/api';
import {
  createBrandMenu,
  deleteBrandMenu,
  getBrandMenus,
  updateBrandMenu,
} from '../../../api/menu.api';
import { reportError } from '../../../helpers/reportError';
import {
  BrandMenuResponse,
  CreateBrandMenuDto,
} from '../../../types/api/menu.types';
import { ResponseType } from '../../../types/shared.types';
import { defaultMenuOf } from '../helpers/brandMenus';

const MENU_PARAM = 'menuId';

// What every multi-menu route answers (400) once the brand's flag is off.
const MULTI_MENU_OFF = 'Multiple menus are not enabled';

const isMultiMenuOff = (error: unknown) =>
  isAxiosError(error) &&
  error.response?.status === 400 &&
  String(error.response.data?.message ?? '').startsWith(MULTI_MENU_OFF);

type MenuListResponse = { data: ResponseType<BrandMenuResponse[]> };

/**
 * The brand's menu list and which one is open. The selection lives in the
 * URL (`?menuId=`) so a refresh, a shared link or the browser's back button
 * land on the same menu; an id the list does not contain (deleted, or a link
 * from another brand) falls back to the default menu.
 *
 * Several menus sit behind a per-brand flag (`multiMenuEnabled` on every
 * row). While it is off the brand works the old way: `menus` is empty, so
 * there is no picker and no "also in other menus" note, and `menuId` is null.
 *
 * `ready` flips once the list request has settled, success or not. Whenever
 * `menuId` is null (flag off, or the request failed) every menu-level call
 * goes out without a `menuId`, which the backend answers with the default
 * menu: the page keeps working exactly as it did before several menus.
 */
export const useBrandMenus = ({
  locationId,
  brandId,
}: {
  locationId: string;
  brandId: string;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [list, setList] = useState<BrandMenuResponse[]>([]);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);

  const menus = list.some((m) => m.multiMenuEnabled) ? list : [];

  const urlMenuId = Number(searchParams.get(MENU_PARAM)) || null;
  const selectedMenu =
    menus.find((m) => m.id === urlMenuId) ?? defaultMenuOf(menus);
  const menuId = selectedMenu?.id ?? null;

  const reload = async () => {
    try {
      const { data } = await getBrandMenus(locationId, brandId);
      setList(data.data);
    } catch (error) {
      reportError(error, { silent: true, context: 'getBrandMenus' });
    } finally {
      setReady(true);
    }
  };

  useEffect(() => {
    setReady(false);
    setList([]);
    reload();
  }, [locationId, brandId]);

  // The flag can be switched off while the page is open. The next request
  // that still names a non-default menu (or manages menus) is refused; the
  // caller shows the error as usual and the list is read again, which drops
  // the page back to the single menu.
  useEffect(() => {
    const interceptor = api.interceptors.response.use(undefined, (error) => {
      if (isMultiMenuOff(error)) reload();
      return Promise.reject(error);
    });
    return () => api.interceptors.response.eject(interceptor);
  }, [locationId, brandId]);

  const select = (id: number) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(MENU_PARAM, String(id));
        return next;
      },
      { replace: true }
    );
  };

  const patchMenu = (id: number, patch: Partial<BrandMenuResponse>) => {
    setList((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  };

  // Every write answers with the whole updated list, so the four actions
  // share one busy flag, one toast and one error path.
  const run = async (
    action: () => Promise<MenuListResponse>,
    after?: (list: BrandMenuResponse[]) => void
  ) => {
    try {
      setBusy(true);
      const { data } = await action();
      setList(data.data);
      SuccessToast(data.message);
      after?.(data.data);
      return true;
    } catch (error) {
      reportError(error);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const createMenu = (dto: CreateBrandMenuDto) => {
    const before = new Set(menus.map((m) => m.id));
    return run(
      () => createBrandMenu(locationId, brandId, dto),
      (list) => {
        // Open the menu that was just made: it is the one id not seen before.
        const created = list.find((m) => !before.has(m.id));
        if (created) select(created.id);
      }
    );
  };

  const renameMenu = (id: number, name: string) =>
    run(() => updateBrandMenu(locationId, brandId, id, { name }));

  const setDefaultMenu = (id: number) =>
    run(() => updateBrandMenu(locationId, brandId, id, { isDefault: true }));

  const removeMenu = (id: number) =>
    run(
      () => deleteBrandMenu(locationId, brandId, id),
      (list) => {
        if (id !== menuId) return;
        const fallback = defaultMenuOf(list);
        if (fallback) select(fallback.id);
      }
    );

  return {
    menus,
    ready,
    busy,
    menuId,
    selectedMenu,
    select,
    patchMenu,
    reload,
    createMenu,
    renameMenu,
    setDefaultMenu,
    removeMenu,
  };
};

export type BrandMenusState = ReturnType<typeof useBrandMenus>;
